import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { createHighlighter, type Highlighter } from "shiki";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Singleton highlighter instance - only load common languages
let highlighterInstance: Highlighter | null = null;
let highlighterPromise: Promise<Highlighter> | null = null;

async function getHighlighter(): Promise<Highlighter> {
  if (highlighterInstance) return highlighterInstance;

  if (!highlighterPromise) {
    highlighterPromise = createHighlighter({
      themes: ["github-light", "github-dark"],
      langs: [
        "javascript",
        "typescript",
        "python",
        "bash",
        "json",
        "html",
        "css",
        "markdown",
        "sql",
      ],
    }).then((hl) => {
      highlighterInstance = hl;
      return hl;
    });
  }

  return highlighterPromise;
}

// Custom code block component using Shiki core with copy button
function CodeBlock({ children, className }: { children: string; className?: string }) {
  const [html, setHtml] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [detectedLang, setDetectedLang] = useState<string>("");
  const { theme } = useTheme();

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    async function highlightCode() {
      try {
        const highlighter = await getHighlighter();
        const language = className?.replace("language-", "") || "text";

        // Check if language is loaded
        const loadedLanguages = highlighter.getLoadedLanguages();
        const langToUse = loadedLanguages.includes(language) ? language : "text";

        // Set detected language for display
        setDetectedLang(langToUse === "text" ? "plain" : langToUse);

        // Use theme based on current theme
        const shikiTheme = theme === "dark" ? "github-dark" : "github-light";

        const result = highlighter.codeToHtml(children.trim(), {
          lang: langToUse,
          theme: shikiTheme,
        });

        setHtml(result);
      } catch (error) {
        console.error("Shiki highlighting error:", error);
        // Fallback to simple styled pre/code
        setHtml(
          `<pre class="mb-4 p-4 bg-muted border rounded-lg overflow-x-auto"><code>${children}</code></pre>`,
        );
      } finally {
        setIsLoading(false);
      }
    }

    highlightCode();
  }, [children, className, theme]);

  if (isLoading) {
    return (
      <pre className="mb-4 p-4 bg-muted border rounded-lg">
        <code className="text-muted-foreground">{children}</code>
      </pre>
    );
  }

  return (
    <div className="not-prose mb-4 border rounded-lg bg-card overflow-hidden">
      <div className="flex justify-between items-center px-3 py-2 bg-muted/50 border-b">
        <span className="text-xs font-medium text-muted-foreground">{detectedLang}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-muted-foreground hover:text-foreground"
          onClick={copyToClipboard}
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              <span className="text-xs">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 mr-1" />
              <span className="text-xs">Copy</span>
            </>
          )}
        </Button>
      </div>
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        className="p-4 [&_pre]:!m-0 [&_pre]:!border-0 [&_pre]:!rounded-none [&_pre]:!bg-transparent [&_pre]:!leading-relaxed [&_code]:!leading-relaxed [&_pre]:!text-sm [&_code]:!text-sm [&_pre]:!whitespace-pre-wrap [&_code]:!whitespace-pre-wrap"
      />
    </div>
  );
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div className={cn("markdown-content prose prose-md max-w-none", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom styling for different elements
          h1: ({ children }) => (
            <h1 className="text-xl font-bold text-foreground mb-4 border-b border-border pb-2">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-lg font-semibold text-foreground mb-3 mt-6">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-md font-semibold text-foreground mb-2 mt-4">{children}</h3>
          ),
          p: ({ children, ...props }) => {
            // Check if this paragraph is inside a list item
            const isInListItem = props.node?.parent?.tagName === "li";
            if (isInListItem) {
              return <span className="text-foreground leading-relaxed">{children}</span>;
            }
            return <p className="text-foreground leading-relaxed">{children}</p>;
          },
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => <em className="italic text-muted-foreground">{children}</em>,
          code: ({ inline, children, className, ...props }) => {
            // Check if this is inline code (not inside pre and not a language class)
            const isInline = inline || !className?.startsWith("language-");

            if (isInline) {
              return (
                <code
                  className="px-2 py-1 text-xs font-bold bg-accent text-accent-foreground rounded-md inline-block align-baseline border border-accent/30"
                  {...props}
                >
                  {children}
                </code>
              );
            }

            // For block code, just return the code element - pre will handle the highlighting
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children, ...props }) => {
            // Check if this pre contains a code element (code block)
            const childArray = React.Children.toArray(children);
            const codeChild = childArray.find(
              (child) =>
                React.isValidElement(child) &&
                (child.type === "code" ||
                  (typeof child.type === "function" && child.type.name === "code") ||
                  child.props?.className?.startsWith("language-")),
            ) as React.ReactElement;

            if (codeChild && codeChild.props?.className?.startsWith("language-")) {
              // This is a code block with language - use our custom Shiki component
              return (
                <CodeBlock className={codeChild.props.className}>
                  {String(codeChild.props.children)}
                </CodeBlock>
              );
            }

            // Regular pre element
            return (
              <pre className="mb-4 p-4 bg-muted border rounded-lg overflow-x-auto" {...props}>
                {children}
              </pre>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-muted-foreground/30 pl-4 py-2 my-4 bg-muted/50 italic text-muted-foreground">
              {children}
            </blockquote>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-outside ml-6 mb-3 space-y-1 text-foreground">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside ml-6 mb-3 space-y-1 text-foreground">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-foreground">{children}</li>,
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-border divide-y divide-border">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
          tbody: ({ children }) => <tbody className="divide-y divide-border">{children}</tbody>,
          tr: ({ children }) => <tr className="hover:bg-muted/50">{children}</tr>,
          th: ({ children }) => {
            // Process children to handle <br> tags
            const processedChildren = React.Children.map(children, (child) => {
              if (typeof child === 'string') {
                // Split by <br> or <br/> or <br /> and create line breaks
                const parts = child.split(/<br\s*\/?>/i);
                return parts.map((part, index) => (
                  <React.Fragment key={index}>
                    {part}
                    {index < parts.length - 1 && <br />}
                  </React.Fragment>
                ));
              }
              return child;
            });
            
            return (
              <th className="px-3 py-2 text-left text-xs font-semibold text-foreground uppercase tracking-wider border-r border-border last:border-r-0">
                {processedChildren}
              </th>
            );
          },
          td: ({ children }) => {
            // Process children to handle <br> tags
            const processedChildren = React.Children.map(children, (child) => {
              if (typeof child === 'string') {
                // Split by <br> or <br/> or <br /> and create line breaks
                const parts = child.split(/<br\s*\/?>/i);
                return parts.map((part, index) => (
                  <React.Fragment key={index}>
                    {part}
                    {index < parts.length - 1 && <br />}
                  </React.Fragment>
                ));
              }
              return child;
            });
            
            return (
              <td className="px-3 py-2 text-sm text-foreground border-r border-border last:border-r-0">
                {processedChildren}
              </td>
            );
          },
          hr: () => <hr className="my-6 border-t border-border" />,
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-primary hover:text-primary/80 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <img src={src} alt={alt} className="max-w-full h-auto rounded-lg shadow-sm mb-4" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
